import { useMutation, useQuery, useReactiveVar } from "@apollo/client";
import { useState } from "react";
import {
  AiSkillsOverviewQuery,
  AiSkillsOverviewQueryVariables,
  SkillVisibilityEnum,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import {
  currentOrganizationVar,
  errorNotificationVar,
  successNotificationVar,
} from "@cache/cache";
import Button from "@components/button/button";
import { DropdownOptionType } from "@components/dropdown/dropdown-types";
import GraphqlError from "@components/error/graphql-error";
import Heading from "@components/heading/heading";
import Loading from "@components/loading/loading";
import { graphqlNone } from "@helpers/constants";

import aiSkillsOverviewQuery from "../../graphql/ai-skills-overview-query";
import archiveSkillMutation from "../../graphql/archive-skill-mutation";
import createOrUpdateSkillMutation from "../../graphql/create-or-update-skill-mutation";
import SkillEditModal from "../skills/skill-edit-modal";
import SkillCard, { SkillCardData } from "./skill-card";
import useAdminOrganization from "./use-admin-organization";

type SkillRow = AiSkillsOverviewQuery["aiSkillsOverview"][number];

const AiSkillsPage = () => {
  const label = useLabel();
  const { canManageOrgAi } = useAdminOrganization();
  // The org whose skills we show is the user's *current* org (works for any
  // member), not the admin-only org from useAdminOrganization. canManageOrgAi
  // still gates whether the member may create org skills.
  const currentOrg = useReactiveVar(currentOrganizationVar);
  const orgId = currentOrg.id !== graphqlNone ? currentOrg.id : null;

  const refetchQueries = [
    {
      query: aiSkillsOverviewQuery,
      variables: { organizationId: orgId ?? null },
    },
  ];

  const { data, loading, error } = useQuery<
    AiSkillsOverviewQuery,
    AiSkillsOverviewQueryVariables
  >(aiSkillsOverviewQuery, {
    fetchPolicy: "cache-and-network",
    variables: { organizationId: orgId ?? null },
  });

  const [archive, { loading: archiving }] = useMutation(archiveSkillMutation, {
    refetchQueries,
  });

  const [copySkill, { loading: copying }] = useMutation(
    createOrUpdateSkillMutation,
    { refetchQueries },
  );

  const [editing, setEditing] = useState<{
    open: boolean;
    skill?: SkillCardData;
    organizationId?: number;
    forkFromSlug?: string;
  }>({ open: false });

  const rows = data?.aiSkillsOverview ?? [];
  const toCardData = (row: SkillRow): SkillCardData => ({
    // Platform skills use their `platform:<slug>` id (aliased to avoid the
    // UUID-vs-String union clash), so "Test" can run the exact built-in.
    id: row.__typename === "SkillNode" ? row.id : row.platformId,
    name: row.name,
    slug: row.slug,
    description: row.description,
    body: row.body,
    canEdit: row.canEdit,
    canArchive: row.canArchive,
    isScheduled: row.isScheduled,
  });

  // Preserve the query's order within each section.
  const yourRows = rows.filter(
    (row) => row.visibility === SkillVisibilityEnum.Personal,
  );
  const orgRows = rows.filter(
    (row) =>
      row.visibility === SkillVisibilityEnum.Workspace ||
      row.visibility === SkillVisibilityEnum.Platform,
  );

  const handleDelete = async (skill: SkillCardData) => {
    const id = skill.id;
    if (!id) return;
    if (!window.confirm(`Delete "${skill.name}"?`)) return;
    try {
      await archive({ variables: { id } });
      successNotificationVar({ title: "Skill deleted" });
    } catch (e) {
      errorNotificationVar({
        title: "Could not delete skill",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  // Platform rows are editable only by org admins (editing forks an org copy);
  // personal/workspace rows defer to the skill's own canEdit.
  const canEditRow = (row: SkillRow) =>
    row.visibility === SkillVisibilityEnum.Platform
      ? canManageOrgAi
      : row.canEdit;

  const openEdit = (row: SkillRow) => {
    const skill = toCardData(row);
    if (row.visibility === SkillVisibilityEnum.Platform) {
      // Fork mode: no id, pinned slug, scoped to the current org. Saving
      // creates an org skill that shadows the platform default.
      setEditing({
        open: true,
        skill,
        organizationId: orgId ?? undefined,
        forkFromSlug: row.slug,
      });
      return;
    }
    setEditing({
      open: true,
      skill,
      // A workspace skill stays scoped to the org; personal is unscoped.
      organizationId:
        row.visibility === SkillVisibilityEnum.Workspace
          ? (orgId ?? undefined)
          : undefined,
    });
  };

  const copyToMySkills = async (row: SkillRow) => {
    try {
      await copySkill({
        variables: {
          forkFromSlug: row.slug,
          organizationId: null,
          name: row.name,
          description: row.description,
          body: row.body,
        },
      });
      successNotificationVar({ title: "Copied to your skills" });
    } catch (e) {
      errorNotificationVar({
        title: "Could not copy skill",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  const buildActions = (row: SkillRow): DropdownOptionType[] => {
    const skill = toCardData(row);
    const actions: DropdownOptionType[] = [
      {
        label: "Test in Topicflow AI",
        onClick: () => {
          // Pre-fill Ask AI with this skill as a chip. `p` leads with
          // `/slug:skillId` so the exact skill runs even when a same-command
          // personal skill would otherwise win by precedence.
          const ref = skill.id
            ? `/${skill.slug}:${skill.id}`
            : `/${skill.slug}`;
          const params = new URLSearchParams({ p: ref });
          window.open(`/ask-ai?${params.toString()}`, "_blank");
        },
      },
    ];
    if (canEditRow(row)) {
      actions.push({ label: "Edit", onClick: () => openEdit(row) });
    }
    // Copy duplicates a non-personal skill into the user's personal skills.
    if (row.visibility !== SkillVisibilityEnum.Personal) {
      actions.push({
        label: "Copy to my skills",
        disabled: copying,
        onClick: () => copyToMySkills(row),
      });
    }
    if (skill.canArchive) {
      actions.push({ type: "divider" });
      actions.push({
        label: "Delete",
        textColor: "text-red-600",
        disabled: archiving,
        onClick: () => handleDelete(skill),
      });
    }
    return actions;
  };

  const orgName =
    currentOrg.name || label("organization", { capitalize: true });

  const renderSection = (sectionRows: SkillRow[]) =>
    sectionRows.length === 0 ? (
      <p className="text-sm text-gray-500">No skills yet.</p>
    ) : (
      <div className="flex flex-col gap-2">
        {sectionRows.map((row) => (
          <SkillCard
            key={row.slug}
            skill={toCardData(row)}
            actions={buildActions(row)}
            onClick={canEditRow(row) ? () => openEdit(row) : undefined}
          />
        ))}
      </div>
    );

  const isInitialLoad = loading && rows.length === 0;

  return (
    <div aria-label="AI skills page">
      <Heading small title="Skills" />

      <p className="text-sm text-gray-600 mt-2 mb-6">
        Reusable skills you can invoke with{" "}
        <code className="bg-gray-100 px-1 rounded-sm">/skill-name</code> in
        Topicflow AI.
      </p>

      <SkillEditModal
        isOpen={editing.open}
        onClose={() => setEditing({ open: false })}
        organizationId={editing.organizationId}
        forkFromSlug={editing.forkFromSlug}
        refetchQueries={refetchQueries}
        initial={
          editing.skill
            ? {
                // When forking a platform skill we create a brand-new org copy,
                // so never carry an id through.
                id: editing.forkFromSlug
                  ? undefined
                  : (editing.skill.id ?? undefined),
                name: editing.skill.name,
                description: editing.skill.description,
                body: editing.skill.body,
              }
            : undefined
        }
      />

      {error && rows.length === 0 ? (
        <GraphqlError
          error={error}
          whatDidNotWork="Your skills could not be loaded."
        />
      ) : isInitialLoad ? (
        <div className="flex justify-center mt-8">
          <Loading mini size="5" />
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          <section aria-label="Your skills">
            <div className="flex items-center justify-between gap-4 mb-3">
              <h2 className="tf-header-sm-dark">Your skills</h2>
              <Button
                theme="text"
                aria-label="New Personal Skill"
                onClick={() => setEditing({ open: true })}
                text="+ New Personal Skill"
              />
            </div>
            {renderSection(yourRows)}
          </section>

          <section aria-label={`${orgName} skills`}>
            <div className="flex items-center justify-between gap-4 mb-3">
              <h2 className="tf-header-sm-dark">{orgName}</h2>
              {canManageOrgAi && orgId && (
                <Button
                  theme="text"
                  aria-label="New Org Skill"
                  onClick={() =>
                    setEditing({ open: true, organizationId: orgId })
                  }
                  text="+ New Org Skill"
                />
              )}
            </div>
            {renderSection(orgRows)}
          </section>
        </div>
      )}
    </div>
  );
};

export default AiSkillsPage;
